import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManagementPeriodsData } from '../../store/apiSlices/managementperiods/managementPeriodsApiParser';
import Box from '@mui/material/Box';
import Dropdown from '../common/Dropdown';
import Modal from '../common/Modal';
import ModalFooter from '../common/ModalFooter';

interface ManagementPeriod {
  id: string | number;
  vesselId?: number;
  vesselName: string;
  manager: string;
  dateRange: string;
  status: 'Active' | 'Inactive';
}

interface EditManagementPeriodModalProps {
  data: ManagementPeriodsData;
  onClose: () => void;
  onSubmit: (_formData: { status: 'Active' | 'Inactive' }) => void;
  managementPeriod: ManagementPeriod | null;
  isEditMode?: boolean;
  isLoading?: boolean;
}

const EditManagementPeriodModal: React.FC<EditManagementPeriodModalProps> = ({
  data: _data,
  onClose,
  onSubmit,
  managementPeriod,
  isEditMode = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<{
    status: 'Active' | 'Inactive';
  }>({
    status: managementPeriod?.status || 'Active',
  });

  // Status options
  const statusOptions = [
    { value: 'Active', label: t('common.active') },
    { value: 'Inactive', label: t('common.inactive') },
  ];

  // Handle field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={
        isEditMode
          ? t('managementPeriods.editManagementPeriod')
          : t('managementPeriods.addManagementPeriod')
      }
      subtitle={
        managementPeriod ? (
          <>
            {t('managementPeriods.managerName')}:{' '}
            <Box component="span" sx={{ fontSize: '13px', display: 'inline', fontWeight: 400 }}>
              {managementPeriod.manager}
            </Box>{' '}
            | {t('managementPeriods.vesselName')}:{' '}
            <Box component="span" sx={{ display: 'inline', fontSize: '13px', fontWeight: 400 }}>
              {managementPeriod.vesselName}
            </Box>
          </>
        ) : (
          ''
        )
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText={
            isLoading ? t('buttons.updating') : isEditMode ? t('buttons.update') : t('buttons.add')
          }
          isLoading={isLoading}
        />
      }
    >
      <Box mt={3}>
        <Dropdown
          label={t('common.status')}
          value={formData.status}
          onChange={(e) => handleFieldChange('status', e.target.value)}
          options={statusOptions}
          placeholder={t('common.selectStatus')}
          disabled={isLoading}
          hasSearch={false}
        />
      </Box>
    </Modal>
  );
};

export default EditManagementPeriodModal;
